import React from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import SystemLog from "views/SystemLog";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import LocalizedUtils from "shared/localized-utils";
import { useTranslation } from "react-i18next";
import SuspenseLoading from "components/SuspenseLoading";
import PrivateRoute from "components/PrivateRoute/indext";
import ROLES from "shared/roles";
import { useDispatch } from "react-redux";
import tokenExists from "shared/functions/token-exists";
import { getSession } from "shared/reducer/authentication";
import Logout from "views/Logout";

const UserRoutes = React.lazy(() => import("views/Users"));
const Login = React.lazy(() => import("views/Login"));
const NotFound = React.lazy(() => import("views/NotFound"));
const ForgottenPassword = React.lazy(() =>
	import("views/Account/ForgottenPassword")
);
const ForgottenPasswordCancel = React.lazy(() =>
	import("views/Account/ForgottenPasswordCancel")
);
const ForgottenPasswordFinish = React.lazy(() =>
	import("views/Account/ForgottenPasswordFinish")
);
const AuditLog = React.lazy(() => import("views/AuditLog"));
const Trailer = React.lazy(() => import("views/Trailer"));
const Activation = React.lazy(() => import("views/Account/Activate"));
const AccountRoutes = React.lazy(() => import("views/Account"));

interface IAppProps {}

const App = (props: IAppProps) => {
	const location = useLocation();
	const { i18n } = useTranslation();
	const dispatch = useDispatch();

	React.useEffect(() => {
		if (tokenExists()) {
			dispatch(getSession());
		}
	}, [dispatch]);

	return (
		<MuiPickersUtilsProvider utils={LocalizedUtils} locale={i18n.language}>
			<React.Suspense fallback={<SuspenseLoading />}>
				<Switch location={location}>
					<Route exact path="/" component={Login} />
					<Route path="/activation/:token" component={Activation} />
					<Route exact path="/reset" component={ForgottenPassword} />
					<Route path="/reset/finish/:token" component={ForgottenPasswordFinish} />
					<Route path="/reset/cancel/:token" component={ForgottenPasswordCancel} />
					<PrivateRoute path="/logout" component={Logout} />
					<PrivateRoute
						path="/users"
						component={UserRoutes}
						hasAnyAuthorities={[ROLES.ADMINISTRATOR]}
					/>
					<PrivateRoute
						path="/account"
						component={AccountRoutes}
						hasAnyAuthorities={[ROLES.ADMINISTRATOR, ROLES.USER]}
					/>
					<PrivateRoute
						path="/audit-log"
						component={AuditLog}
						hasAnyAuthorities={[ROLES.ADMINISTRATOR]}
					/>
					<PrivateRoute
						path="/system-log"
						component={SystemLog}
						hasAnyAuthorities={[ROLES.ADMINISTRATOR]}
					/>
					<PrivateRoute
						path="/dashboard"
						component={Trailer}
						hasAnyAuthorities={[ROLES.ADMINISTRATOR, ROLES.USER]}
					/>
					<Route component={NotFound} />
				</Switch>
			</React.Suspense>
		</MuiPickersUtilsProvider>
	);
};

export default App;
